import React from "react";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import Banner from "@cs/components/src/Banner";
import VariableInput from "./VariableInput";
import InputGroup from "@cs/components/src/InputGroup";

type Value = { variables: VariableInstance[]; titles?: TitleInstance[] };

interface Props<T extends Value> {
  value: T;
  config?: PluginConfigItem | ScriptConfigItem | ListInputConfig;
  onChange: (value: T) => void;
  noMargin?: boolean;
}

const Description = styled.div`
  color: var(--gray100);
  margin-bottom: 25px;
  font-size: 14px;
  margin-top: 5px;
  white-space: pre-wrap;
`;

const StyledInputGroup = styled(InputGroup)<{ $noMargin?: boolean }>`
  padding-top: 10px;

  ${(props) =>
    !props.$noMargin
      ? css`
          margin: 10px 10px;
        `
      : css`
          margin: 0;
        `}
`;

const VariableInputForm = <T extends Value>(props: Props<T>) => {
  const { onChange, config, value: instance, noMargin } = props;
  const { systemDomainId } = useParams();

  return (
    <StyledInputGroup padHorizontal $noMargin={noMargin}>
      {config && "description" in config && config.description && (
        <Description>{config.description}</Description>
      )}
      {!config?.variables.length && (
        <Banner placeholder="Nothing to configure" />
      )}
      {config?.variables.map((variable) => {
        const variableId = variable.id ?? variable.key;
        const variableInstance = instance.variables.find(
          ({ variableConfigId, dataType }) =>
            variableConfigId === variableId && dataType === variable.dataType
        );
        return (
          <VariableInput
            variable={variable}
            key={variableId}
            value={variableInstance?.value}
            systemDomainId={systemDomainId}
            variables={instance.variables}
            config={config}
            onChange={(value) => {
              const obj = {
                variableConfigId: variableId,
                value,
                dataType: variable.dataType,
              };
              if (variableInstance) {
                onChange({
                  ...instance,
                  variables: instance.variables.map((variableInstance) =>
                    variableInstance.variableConfigId === variableId &&
                    variableInstance.dataType === variable.dataType
                      ? obj
                      : variableInstance
                  ),
                });
              } else {
                onChange({
                  ...instance,
                  variables: [...instance.variables, obj],
                });
              }
            }}
          />
        );
      })}
    </StyledInputGroup>
  );
};

export default VariableInputForm;
